import React, { useRef } from 'react';
import { Pressable, StyleSheet, TextInput, type KeyboardTypeOptions } from 'react-native';
import { Text } from './Texte';
import { colors, formes, spacing, typography } from '../theme/tokens';
import { TEXTE_LTR } from '../i18n/sens';

export interface ChampEtiqueteProps {
  label: string;
  value: string;
  onChangeText: (v: string) => void;
  placeholder?: string;
  multiline?: boolean;
  keyboardType?: KeyboardTypeOptions;
  maxLength?: number;
  // Pour une ligne qui vient d'être ajoutée : le clavier s'ouvre dessus et l'écran la montre.
  autoFocus?: boolean;
  // Texte du CV lui-même (pas de l'interface) : saisi de gauche à droite même en arabe.
  ltr?: boolean;
  /** Le formulaire s'en sert pour amener le champ actif au-dessus du clavier. */
  onFocus?: () => void;
  testID?: string;
}

/**
 * Champ de saisie des formulaires refaits : l'étiquette est DANS le cadre, au-dessus de la valeur.
 * Toucher n'importe où dans le cadre place le curseur dans le champ (la cible fait au moins 44 pt).
 * L'étiquette est aussi l'étiquette d'accessibilité : une placeholder disparaît dès la première
 * lettre, l'étiquette reste.
 */
export function ChampEtiquete({
  label,
  value,
  onChangeText,
  placeholder,
  multiline = false,
  keyboardType,
  maxLength,
  autoFocus,
  ltr,
  onFocus,
  testID,
}: ChampEtiqueteProps) {
  const champ = useRef<TextInput>(null);
  return (
    <Pressable
      accessible={false}
      onPress={() => champ.current?.focus()}
      style={[styles.cadre, multiline && styles.cadreLong]}
    >
      <Text style={styles.label}>{label}</Text>
      <TextInput
        ref={champ}
        testID={testID}
        accessibilityLabel={label}
        style={[styles.saisie, multiline && styles.saisieLongue, ltr && TEXTE_LTR]}
        value={value}
        onChangeText={onChangeText}
        placeholder={placeholder}
        placeholderTextColor={colors.grey500}
        multiline={multiline}
        keyboardType={keyboardType}
        maxLength={maxLength}
        autoFocus={autoFocus}
        onFocus={onFocus}
      />
    </Pressable>
  );
}

const styles = StyleSheet.create({
  cadre: {
    minHeight: formes.hauteur.md + 4,
    justifyContent: 'center',
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    borderRadius: formes.rayon.md,
    borderWidth: 1,
    borderColor: colors.bordureClaire,
    backgroundColor: colors.white,
  },
  cadreLong: { justifyContent: 'flex-start' },
  label: {
    fontSize: typography.caption.size,
    fontWeight: '700',
    color: colors.grey700,
  },
  saisie: {
    minHeight: formes.cible - 12,
    padding: 0,
    fontSize: typography.body.size + 1,
    fontWeight: '500',
    color: colors.night,
  },
  saisieLongue: { minHeight: 92, textAlignVertical: 'top' },
});
